import Phaser from 'phaser';
import { DPR } from '@constants/common.const';
import {
  Tooltip,
  TooltipCaretAlign,
  TooltipCaretConfig,
  TooltipCaretSide,
} from './Tooltip';

export interface TooltipStackConfig {
  scene: Phaser.Scene;
  /** World-space X of the caret tip anchor. */
  x: number;
  /** World-space Y of the caret tip anchor. */
  y: number;
  caretSide?: TooltipCaretSide;
  caretAlign?: TooltipCaretAlign;
  /** Max text width in px (before DPR), forwarded to each tooltip. Ignored when width is set. */
  maxWidth?: number;
  /** Fixed bubble width in px (before DPR), forwarded to each tooltip. */
  width?: number;
  /** Visual gap between stacked tooltip bubbles in px (before DPR). Default: 8. */
  gap?: number;
  depth?: number;
  /** Duration of slide and fade-in animations in ms. Default: 200. */
  animationDurationMs?: number;
}

export interface TooltipStackEntryConfig {
  text: string;
  autoHideDelayMs?: number | false;
  fadeOutDurationMs?: number;
  closeOnClick?: boolean;
}

export class TooltipStackEntry {
  private readonly _tooltip: Tooltip;
  private readonly _onRemove: (entry: TooltipStackEntry) => void;
  private readonly _onReflow: () => void;

  constructor(
    tooltip: Tooltip,
    onRemove: (entry: TooltipStackEntry) => void,
    onReflow: () => void,
  ) {
    this._tooltip = tooltip;
    this._onRemove = onRemove;
    this._onReflow = onReflow;
  }

  /** Live-update the tooltip text. Triggers a stack reflow if height changed. */
  setText(text: string): this {
    this._tooltip.setText(text);
    this._onReflow();
    return this;
  }

  /** Remove this tooltip from the stack (fades out, then destroys). */
  remove(): void {
    this._onRemove(this);
  }

  /** @internal Direct access for TooltipStack positioning. */
  get tooltip(): Tooltip {
    return this._tooltip;
  }
}

export class TooltipStack {
  private readonly scene: Phaser.Scene;
  private readonly anchorX: number;
  private readonly anchorY: number;
  private readonly caretConfig: TooltipCaretConfig;
  private readonly maxWidth?: number;
  private readonly width?: number;
  private readonly gap: number;
  private readonly depth?: number;
  private readonly animDuration: number;

  private readonly entries: TooltipStackEntry[] = [];
  private readonly moveTweens = new Map<TooltipStackEntry, Phaser.Tweens.Tween>();

  constructor(config: TooltipStackConfig) {
    this.scene = config.scene;
    this.anchorX = config.x;
    this.anchorY = config.y;
    this.caretConfig = {
      side: config.caretSide ?? 'bottom',
      align: config.caretAlign ?? 'center',
    };
    this.maxWidth = config.maxWidth;
    this.width = config.width;
    this.gap = config.gap ?? 8;
    this.depth = config.depth;
    this.animDuration = config.animationDurationMs ?? 200;
  }

  /**
   * Add a new tooltip to the bottom of the stack (closest to the anchor).
   * Existing tooltips slide up; the new one slides up from the anchor and fades in.
   */
  push(config: TooltipStackEntryConfig): TooltipStackEntry {
    // Start at anchor — reflow will tween it upward to its final center.
    const tooltip = new Tooltip({
      scene: this.scene,
      x: this.anchorX,
      y: this.anchorY,
      text: config.text,
      maxWidth: this.maxWidth,
      width: this.width,
      depth: this.depth,
      autoHideDelayMs: config.autoHideDelayMs,
      fadeOutDurationMs: config.fadeOutDurationMs,
      closeOnClick: config.closeOnClick,
      onHide: () => this.removeEntry(entry),
    });

    // `entry` is captured by the onHide closure above — assigned on the next line,
    // before the tooltip can possibly fire onHide.
    const entry = new TooltipStackEntry(
      tooltip,
      (e) => this.removeEntry(e),
      () => this.reflow(false),
    );

    this.entries.push(entry);
    this.reflow(true);

    // show() sets visible + starts auto-hide timer. We then override alpha to 0
    // so our fade-in tween controls the appearance. All synchronous — no frame flicker.
    tooltip.show();
    tooltip.setAlpha(0);
    this.scene.tweens.add({
      targets: tooltip,
      alpha: 1,
      duration: this.animDuration,
      ease: 'Linear',
    });

    return entry;
  }

  /**
   * Fade out and remove all tooltips simultaneously.
   * Each tooltip's onHide fires but is a no-op once the array is cleared.
   */
  clear(): void {
    const toFade = [...this.entries];
    this.moveTweens.clear();
    this.entries.length = 0;
    for (const entry of toFade) {
      entry.tooltip.fadeOutAndHide();
    }
  }

  /** Immediately destroy all tooltips without fading. */
  destroy(): void {
    this.moveTweens.forEach((tween) => tween.stop());
    this.moveTweens.clear();
    for (const entry of this.entries) {
      entry.tooltip.destroy();
    }
    this.entries.length = 0;
  }

  private removeEntry(entry: TooltipStackEntry): void {
    const idx = this.entries.indexOf(entry);
    if (idx === -1) return;
    this.cancelMoveTween(entry);
    this.entries.splice(idx, 1);
    this.reflow(true);
  }

  private cancelMoveTween(entry: TooltipStackEntry): void {
    const tween = this.moveTweens.get(entry);
    if (tween) {
      tween.stop();
      this.moveTweens.delete(entry);
    }
  }

  /**
   * Recalculate every tooltip's caret state and world position.
   *
   * Layout (bottom-to-top, index 0 = oldest = highest):
   *   entries[last] = newest = bottom, has caret, caret tip at (anchorX, anchorY)
   *   entries[0]    = oldest = top, no caret
   *
   * When animate=true, each tooltip tweens from its current y to the new target.
   * Cancels any in-progress move tween for that entry first.
   */
  private reflow(animate: boolean): void {
    if (this.entries.length === 0) return;

    const gapPx = this.gap * DPR;

    for (let i = 0; i < this.entries.length; i++) {
      if (i === this.entries.length - 1) {
        this.entries[i].tooltip.setCaret(this.caretConfig);
      } else {
        this.entries[i].tooltip.removeCaret();
      }
    }

    let currentAnchorY = this.anchorY;

    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i];
      const h = entry.tooltip.height;
      const centerY = currentAnchorY - h / 2;

      if (animate) {
        this.cancelMoveTween(entry);
        const tween = this.scene.tweens.add({
          targets: entry.tooltip,
          y: centerY,
          duration: this.animDuration,
          ease: 'Cubic.easeOut',
          onComplete: () => this.moveTweens.delete(entry),
        });
        this.moveTweens.set(entry, tween);
      } else {
        entry.tooltip.setPosition(this.anchorX, centerY);
      }

      currentAnchorY = centerY - h / 2 - gapPx;
    }
  }
}
